import { useEffect, useState } from 'react'
import {
  Button, Checkbox, CircularProgress, FormControl, FormControlLabel, FormGroup,
  Grid, InputLabel, MenuItem, Select, TextField, Typography,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2'
import { useTranslation } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'
import { isValid } from 'date-fns'
import { getFormContainer } from 'container/form-module'
import { FormService } from 'modules/Form/services/FormService'
import { FORM_SERVICE_KEY } from 'modules/Form'
import { FormRow, NewProductDTO } from 'modules/Form/models/Form'
import { Product } from 'modules/product/models/Product'
import { PatientProduct } from 'modules/patient/models/relationship/PatientProduct'
import { SampleTypeService } from 'modules/sampleType/services/SampleTypeService'
import { SAMPLETYPE_SERVICE_KEY } from 'modules/sampleType'
import { SampleType } from 'modules/sampleType/models/SampleType'
import { Query } from 'common/api/Query'
import { SampleCustom } from 'modules/sampleType/models/sampleForm/SampleCustom'
import { emptySampleCustomDTO } from 'modules/sampleType/models/sampleForm/SampleCustomDTO'
import { convertTimeToLocal, formatDate } from '../../../common/utils/DataFormatters'
import { useStyles } from '../Application.styles'
import { COLOR_PRIMARY } from '../../../routes/color-constants'
import { getSampleTypeContainer } from 'container/sampleType-modules'

type Props = {
  patientProduct: PatientProduct
  product: Product
  handleChangePatientProducts: (pp: PatientProduct, i: number) => void
  index: number
  edit?: boolean
}

const formService = getFormContainer().get<FormService>(FORM_SERVICE_KEY)
const sampleTypeService = getSampleTypeContainer().get<SampleTypeService>(SAMPLETYPE_SERVICE_KEY)

function emptySample(rows: FormRow[]): SampleCustom {
  const data = rows.flatMap(row =>
    row.items.map(item => ({
      key: item.field.name,
      value: item.field.type === 'checkbox' ? false : item.field.type === 'checkboxGroup' ? [] : '',
    }))
  )
  return new SampleCustom({ _id: uuidv4(), _typeSample: '', _sampleData: data })
}

export const SampleCustomForm = (props: Props) => {
  const { classes } = useStyles({ color: COLOR_PRIMARY })
  const { t, i18n } = useTranslation()
  const [formDef, setFormDef] = useState<NewProductDTO | null>(null)
  const [loading, setLoading] = useState(true)
  const [sampleTypes, setSampleTypes] = useState<SampleType[]>([])
  const [loadingTypes, setLoadingTypes] = useState(true)

  const patientNumber = props.product?.patientNumber ?? 1
  const patients = props.patientProduct.patientCustoms || []

  useEffect(() => {
    const typeForm = props.product?.typeForm
    if (!typeForm) { setLoading(false); return }
    formService.byTypeForm(typeForm).subscribe((res) => {
      if (res) setFormDef(res)
      setLoading(false)
    })
  }, [props.product?.typeForm])

  useEffect(() => {
    sampleTypeService.getFilteredListDB(new Query({})).subscribe((res) => {
      setSampleTypes(res.items)
      setLoadingTypes(false)
    })
  }, [])

  const getSampleData = (patientIndex: number): SampleCustom => {
    const raw = patients[patientIndex]?._sample
    if (!raw) return emptySample(formDef?.sampleForm || [])
    // Si ya es instancia con métodos, devolver tal cual
    if (typeof (raw as any).getFieldValue === 'function') return raw as SampleCustom
    // Objeto plano: reconstruir como instancia
    const rawAny = raw as any
    return new SampleCustom({
      _id: rawAny._id ?? undefined,
      _typeSample: rawAny._typeSample ?? '',
      _sampleData: Array.isArray(rawAny._sampleData)
        ? rawAny._sampleData.map((f: any) => ({ key: f.key ?? '', value: f.value ?? '' }))
        : [],
    })
  }

  const handleSampleTypeChange = (patientIndex: number, idOdoo: string) => {
    const pp = props.patientProduct
    const updatedPatients = [...(pp.patientCustoms || [])]
    const patient = updatedPatients[patientIndex]
    if (!patient) return
    const sample = getSampleData(patientIndex)
    sample._typeSample = idOdoo
    patient._sample = sample
    pp.patientCustoms = updatedPatients
    props.handleChangePatientProducts(pp, props.index)
  }

  const handleFieldChange = (patientIndex: number, fieldName: string, value: any) => {
    const pp = props.patientProduct
    const updatedPatients = [...(pp.patientCustoms || [])]
    const patient = updatedPatients[patientIndex]
    if (!patient) return
    const sample = getSampleData(patientIndex)
    sample.setFieldValue(fieldName, value)
    patient._sample = sample
    pp.patientCustoms = updatedPatients
    props.handleChangePatientProducts(pp, props.index)
  }

  const renderField = (
    patientIndex: number,
    fieldName: string,
    field: FormRow['items'][0]['field'],
    width: number,
  ) => {
    const sample = getSampleData(patientIndex)
    const value = sample?.getFieldValue(fieldName) ?? ''
    const disabled = props.edit === false

    switch (field.type) {
      case 'text':
      case 'number':
      case 'email':
        return (
          <Grid item xs={12} md={width} key={fieldName}>
            <TextField
              fullWidth variant="filled" label={t(field.title) || field.title}
              type={field.type} disabled={disabled} required={field.isRequired}
              value={value}
              onChange={(e) => handleFieldChange(patientIndex, fieldName, e.target.value)}
            />
          </Grid>
        )
      case 'textarea':
        return (
          <Grid item xs={12} md={width} key={fieldName}>
            <TextField
              fullWidth multiline rows={4} variant="outlined"
              label={t(field.title) || field.title} disabled={disabled}
              required={field.isRequired} value={value}
              onChange={(e) => handleFieldChange(patientIndex, fieldName, e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        )
      case 'date':
        return (
          <Grid item xs={12} md={width} key={fieldName}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                disabled={disabled} format="dd/MM/yyyy"
                label={t(field.title) || field.title}
                value={convertTimeToLocal(value) || null}
                onChange={(date) =>
                  date && isValid(date) && handleFieldChange(patientIndex, fieldName, formatDate(date))
                }
                slotProps={{ textField: { size: 'small', fullWidth: true, required: field.isRequired } }}
              />
            </LocalizationProvider>
          </Grid>
        )
      case 'select':
        return (
          <Grid item xs={12} md={width} key={fieldName}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>{(t(field.title) || field.title) + (field.isRequired ? ' *' : '')}</InputLabel>
              <Select
                label={(t(field.title) || field.title) + (field.isRequired ? ' *' : '')}
                value={value} disabled={disabled} required={field.isRequired}
                onChange={(e) => handleFieldChange(patientIndex, fieldName, e.target.value)}
              >
                <MenuItem value=""><em>{t('notSelect')}</em></MenuItem>
                {(field.options ?? []).map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.text}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )
      case 'checkbox':
        return (
          <Grid item xs={12} md={width} key={fieldName}>
            <FormControlLabel
              control={<Checkbox checked={!!value} disabled={disabled}
                onChange={(_, checked) => handleFieldChange(patientIndex, fieldName, checked)} />}
              label={t(field.title) || field.title}
            />
          </Grid>
        )
      case 'checkboxGroup': {
        const selected: string[] = Array.isArray(value) ? value : []
        return (
          <Grid item xs={12} md={width} key={fieldName}>
            <Typography variant="body2" gutterBottom>
              {(t(field.title) || field.title) + (field.isRequired ? ' *' : '')}
            </Typography>
            <FormGroup row>
              {(field.options ?? []).map((opt) => (
                <FormControlLabel key={opt.value}
                  control={<Checkbox size="small" checked={selected.includes(opt.value)} disabled={disabled}
                    onChange={(_, checked) => {
                      const next = checked ? [...selected, opt.value] : selected.filter((v) => v !== opt.value)
                      handleFieldChange(patientIndex, fieldName, next)
                    }} />}
                  label={opt.text}
                />
              ))}
            </FormGroup>
          </Grid>
        )
      }
      default:
        return null
    }
  }

  if (loading || loadingTypes) {
    return <CircularProgress style={{ alignSelf: 'center' }} />
  }

  if (!formDef?.sampleForm?.length) {
    return <Typography color="text.secondary">{t('noPatientFormDefined')}</Typography>
  }

  return (
    <Grid container direction="column">
      {[...Array(patientNumber)].map((_, patientIndex) => (
        <Grid container spacing={4} key={patientIndex} style={{ marginTop: '40px' }}>
          <Grid item xs={12} style={{ textAlign: 'left', fontWeight: 'bold', fontSize: '18px' }}>
            {t('fillSampleData') + ' ' + (patientIndex + 1) + ' ' + t('for') + ' ' +
              (i18n.language === 'es' ? props.product.nameES : props.product.nameEN)}
          </Grid>
          <Grid item xs={12}>
            <FormControl variant="filled" fullWidth>
              <InputLabel id={`typeSampleLabel-${patientIndex}`}>{t('typeSample') + '*'}</InputLabel>
              <Select
                labelId={`typeSampleLabel-${patientIndex}`}
                disabled={props.edit === false}
                value={patients[patientIndex]?._sample?._typeSample ?? ''}
                onChange={(e) => handleSampleTypeChange(patientIndex, e.target.value as string)}
              >
                {sampleTypes.map((st) => (
                  <MenuItem key={st.idOdoo} value={st.idOdoo || ''}>
                    {i18n.language === 'es' ? st.nameES : st.nameEN}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {formDef.sampleForm.map((row) =>
            row.items.map((item) =>
              renderField(patientIndex, item.field.name, item.field, item.width)
            )
          )}
        </Grid>
      ))}
    </Grid>
  )
}
